import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { BranchTaxDefaultsEditService } from './branch-tax-defaults-edit.service';

@Component({
    selector: 'gtw-branch-tax-defaults-edit',
    templateUrl: './branch-tax-defaults-edit.component.html',
    styleUrls: ['./branch-tax-defaults-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BranchTaxDefaultsEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  minimise = false;
  loading = true;
  isSaveClicked = false;

  formData: any = {
    filterTaxYear: '',
    scenario: ''
  };

  defaultsMultipleSelected: any[] = [];
  gridDataMultipleSelect: any = null;
  basketCatgData: any[] = [];
  countryData: any[] = [];
  taxYearData: any[] = [];
  basketCodeDefault: any[] = [];
  attribMastData: any[] = [];
  is_locked: any = null;
  logged_in_user: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private branchTaxDefaultsService: BranchTaxDefaultsEditService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.defaultsMultipleSelected = [];
    this.gridDataMultipleSelect = null;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData || {};
    this.appService.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const gridData = gridPayloadFromActiveScreen(activeScreen);
    this.gridDataMultipleSelect = gridData ? _.clone(gridData) : null;
  }

  init() {
    try {
      if (this.GENERAL_CONFIG?.base_url) {
        this.loadInitialData();
      }
    } catch (e) {
      console.error('Error in branch-tax-defaults-edit init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  loadInitialData() {
    this.formData.filterTaxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;
    this.formData.scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
    this.logged_in_user = this.appService.USER_SETTINGS?.sso_id || this.appService.USER_SETTINGS?.user?.sso_id || '';

    this.getCreditableDropDown();
    this.getAttribMast();
    this.checkLockStatus();
    this.processGridData();
  }

  checkLockStatus() {
    if (!this.GENERAL_CONFIG?.base_url || !this.filterParams?.scenario || !this.globalService?.globalParams?.jcd_key) return;

    const lockparams = {
      'action_code': 'n10cpv',
      'p_scenario': this.filterParams.scenario,
      'p_jcd_key': this.globalService.globalParams.jcd_key,
      'p_consol_group_key': this.filterParams.filing_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.is_locked = response.jsonObject;
          if (this.is_locked?.[0]?.LOCK_STATUS === 'Y') {
            this.gtwSnackbar.openSnackBar(`Tax Return has already been filed for tax year ${this.formData.filterTaxYear}.`, 'gtw-snackbar--red', true, 4);
            this.cancel();
          }
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  getCreditableDropDown() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    const params = { 'action_code': 'e83e1x' };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.basketCatgData = _.filter(response.jsonObject, (p: any) => _.includes(['SOURCE_CODE'], p.CODE_GROUP));
          this.basketCodeDefault = _.filter(this.basketCatgData, (p: any) => _.includes(['BRANCH'], p.CODE_SHORT_DESC));
          this.countryData = _.filter(response.jsonObject, (p: any) => _.includes(['COUNTRY_CODE'], p.CODE_GROUP));
          this.taxYearData = _.filter(response.jsonObject, (p: any) => _.includes(['TAX_YEAR'], p.CODE_GROUP));

          this.applyBasketDefaults();
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading creditable dropdown data:', error);
      }
    );
  }

  private applyBasketDefaults() {
    if (this.basketCodeDefault?.length > 0) {
      for (const t of this.defaultsMultipleSelected) {
        t.BASKET = this.basketCodeDefault[0].CODE_DISP_DESC;
        t.BASKET_KEY = this.basketCodeDefault[0].CODE_KEY;
      }
    }
  }

  getAttribMast() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    const params = { 'action_code': 'e0vg4g' };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.attribMastData = response.jsonObject;
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading attribute master data:', error);
      }
    );
  }

  processGridData() {
    this.defaultsMultipleSelected = [];

    if (!this.gridDataMultipleSelect) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    const gridItems = Array.isArray(this.gridDataMultipleSelect)
      ? this.gridDataMultipleSelect
      : (this.gridDataMultipleSelect ? [this.gridDataMultipleSelect] : []);

    for (const item of gridItems) {
      const rowData = item?.data ?? item?.rowData ?? item;
      if (!rowData) continue;

      const entry = _.cloneDeep(rowData);
      entry.progress = 'Save_Defaults';
      entry.OPERATION_TYPE = 'S';

      if (entry.ORIG_CREDIT_AMT !== undefined) {
        entry.CREDIT_AMT = entry.ORIG_CREDIT_AMT;
      }

      if (entry.IS_DEFAULT_TAG === 'Y') {
        entry.BACKGROUND_COLOR = 'warning';
        entry.STATUS_TEXT = 'Defaults Already Applied';
      } else if (
        entry.IS_DEFAULT_TAG === 'N' &&
        (!entry.CREDIT_AMT || entry.CREDIT_AMT === 0) &&
        (!entry.NON_CREDIT_AMT || entry.NON_CREDIT_AMT === 0)
      ) {
        entry.BACKGROUND_COLOR = '';
        entry.STATUS_TEXT = '';
      } else if (
        entry.IS_DEFAULT_TAG === 'N' &&
        ((entry.CREDIT_AMT && entry.CREDIT_AMT !== 0) ||
         (entry.NON_CREDIT_AMT && entry.NON_CREDIT_AMT !== 0))
      ) {
        entry.BACKGROUND_COLOR = 'danger';
        entry.STATUS_TEXT = 'Override Existing Sourcing ?';
      }

      entry.ORIG_CREDIT_AMT = this.formatNumber(entry.CREDIT_AMT);
      entry.TB_AMT = ('' + entry.TB_AMT).replace(/,/g, '');
      entry.CREDIT_AMT = entry.TB_AMT;
      entry.BASKET = '';
      entry.BASKET_KEY = '';
      entry.CURRENT_TAX_YEAR = this.formData.filterTaxYear;

      this.defaultsMultipleSelected.push(entry);
    }

    this.applyBasketDefaults();
    this.loading = false;
    this.cdr.detectChanges();
  }

  formatNumber(value: any): string {
    if (value == null || value === undefined) return '0';
    const num = parseInt(('' + value).replace(/,/g, ''), 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  removeDefaultsRow(entry: any) {
    const index = this.defaultsMultipleSelected.indexOf(entry);
    if (index > -1) {
      this.defaultsMultipleSelected[index].CREDIT_AMT = this.defaultsMultipleSelected[index].ORIG_CREDIT_AMT;
      this.defaultsMultipleSelected.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  private getCountryKey(countryDesc: string): string {
    const country = this.countryData.find((c: any) =>
      c.CODE_DISP_DESC && c.CODE_DISP_DESC.includes(countryDesc)
    );
    return country ? '' + country.CODE_KEY : '';
  }

  private getTaxYearKey(taxYearDesc: any): string {
    const taxYear = this.taxYearData.find((t: any) => t.CODE_DISP_DESC == taxYearDesc);
    return taxYear ? '' + taxYear.CODE_KEY : '';
  }

  save() {
    if (this.is_locked?.[0]?.LOCK_STATUS === 'Y') {
      this.gtwSnackbar.openSnackBar(`Tax Return has already been filed for tax year ${this.formData.filterTaxYear}.`, 'gtw-snackbar--red', true, 4);
      this.cancel();
      return;
    }

    if (this.defaultsMultipleSelected.length === 0) {
      this.gtwSnackbar.openSnackBar('Please select at least one Branch Tax Default Mapping', 'gtw-snackbar--red', true, 4);
      return;
    }

    this.isSaveClicked = true;
    this.cdr.detectChanges();

    const returnClobSettingsObj: any = {
      sso_id: this.logged_in_user,
      scenario: this.formData.scenario
    };

    const defaultsChangeDetails: any[] = [];

    for (const entry of this.defaultsMultipleSelected) {
      const returnObj: any = {};
      returnObj['TAG_KEY'] = '';
      returnObj['COMBINATION_KEY'] = entry.COMBINATION_KEY;
      returnObj['OPERATION_TYPE'] = 'S';

      const basketAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'BASKET']);
      const genLeidAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'GEN_LEID']);
      const countryAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'COUNTRY']);
      const applicableYearAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'APPLICABLE_YEAR']);
      const explanationAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'EXPLANATION_CREDITABLE_US_ENTITY']);

      if (basketAttrib) returnObj[basketAttrib.ATTRIB_KEY + '_0'] = entry.BASKET_KEY;
      if (genLeidAttrib) returnObj[genLeidAttrib.ATTRIB_KEY + '_0'] = entry.LEID;
      if (countryAttrib) returnObj[countryAttrib.ATTRIB_KEY + '_0'] = this.getCountryKey(entry.LE_COUNTRY_NAME);
      if (applicableYearAttrib) returnObj[applicableYearAttrib.ATTRIB_KEY + '_0'] = this.getTaxYearKey(entry.CURRENT_TAX_YEAR);
      if (explanationAttrib) returnObj[explanationAttrib.ATTRIB_KEY + '_0'] = 'Not Applicable';

      returnObj['IS_CREDITABLE'] = 'Y';
      returnObj['AMOUNT'] = (entry.CREDIT_AMT != null) ? entry.CREDIT_AMT : 0;
      returnObj['GROUP_ID'] = 1;

      defaultsChangeDetails.push(returnObj);
    }

    this.branchTaxDefaultsService.savePivotedData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      defaultsChangeDetails,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Defaults have been applied and saved/updated', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        console.error('Error saving defaults:', error);
        const errorMessage = error?.error?.errorMessage || error?.errorMessage ||
          error?.error?.data?.errorMessage ||
          'Error saving defaults. Please try again.';
        this.gtwSnackbar.openSnackBar(errorMessage, 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
